import React from 'react';
import { useSettings } from './context';
import { S } from '../../styles';

// Payments — extracted from Settings.jsx on 17 Sep 2026. The markup is
// unchanged; every value it needs comes from the Settings context rather than
// from props, so this move could not alter behaviour.
export default function SettingsPayments() {
  const { T, allStatuses, paymentTypes, setPayments, settings, taxClasses } = useSettings();
  return (
    <>
      <div style={{ ...S.settingsCard, background:T.card, border:`1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color:T.text }}>Payments</div>
        <div style={{ ...S.settingsCardDesc, color:T.textSub }}>
          Controls textable payment links — the links staff send customers to pay a ticket or put money on account.
        </div>

        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, minWidth:200, color:T.text }}>Status when a ticket is paid</span>
          <select value={(settings.payments || {}).paidStatusId || ''}
            onChange={e => setPayments({ paidStatusId: e.target.value || null })}
            style={{ flex:1, background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'7px 12px', color:T.text, fontSize:13, outline:'none' }}>
            <option value="">Don't change the status</option>
            {allStatuses
              .filter(s => {
                // "Done & Paid" is TERMINAL in Lightspeed — it closes the ticket,
                // assumes payment in full, and cannot be undone. Spinboard must
                // never write it automatically: a partial payment or deposit would
                // silently close a ticket with work and money still outstanding.
                return (s.name || '').toLowerCase() !== 'done & paid';
              })
              .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div style={{ fontSize:11, color:T.textSub, marginBottom:14, fontStyle:'italic', paddingLeft:2 }}>
          ⓘ When a customer pays a ticket's payment link, Spinboard moves the ticket to this status automatically — so whoever rings them out at the counter can see they've already paid. Lightspeed's "Done &amp; Paid" isn't available here: it closes the ticket permanently and assumes payment in full, which a partial payment or deposit shouldn't do.
        </div>

        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, minWidth:200, color:T.text }}>Deposit payment type</span>
          <select value={(settings.payments || {}).depositPaymentTypeId || ''}
            onChange={e => setPayments({ depositPaymentTypeId: e.target.value || null })}
            style={{ flex:1, background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'7px 12px', color:T.text, fontSize:13, outline:'none' }}>
            <option value="">— Select a payment type —</option>
            {paymentTypes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div style={{ fontSize:11, color:T.textSub, marginBottom:14, fontStyle:'italic', paddingLeft:2 }}>
          ⓘ Which Lightspeed payment type online payments are recorded under. <strong>Create a dedicated type for this</strong> (Settings → Payment Types in Lightspeed) — don't reuse Credit Card, or your register's card totals will include money that never went through your terminal and your end-of-day won't reconcile. Until this is set, online payments collect in Stripe but can't be written to the customer's Lightspeed account.
        </div>
        {!((settings.payments || {}).depositPaymentTypeId) && (
          <div style={{ fontSize:11.5, color:'#e74c3c', fontWeight:600, marginBottom:14, paddingLeft:2 }}>
            ⚠ Not set — online payments will fail to record in Lightspeed until you choose one.
          </div>
        )}
        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, minWidth:200, color:T.text }}>Misc charge tax class</span>
          <select value={(settings.payments || {}).miscTaxClassId || ''}
            onChange={e => setPayments({ miscTaxClassId: e.target.value || null })}
            style={{ flex:1, background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'7px 12px', color:T.text, fontSize:13, outline:'none' }}>
            <option value="">— Not set —</option>
            {taxClasses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div style={{ fontSize:11, color:T.textSub, marginBottom:14, fontStyle:'italic', paddingLeft:2 }}>
          ⓘ Which tax class marks a line as a <strong>Misc charge</strong> rather than Labor. Lightspeed exposes no type field on these lines, so Spinboard tells them apart by tax class — and it only accepts a class named <strong>Miscellaneous</strong> or one of Labor type. Leave this unset unless you have a reason not to: Spinboard finds your Miscellaneous class automatically. Set it only if your shop uses a differently-named class.
        </div>

        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, minWidth:200, color:T.text }}>Payment links expire after</span>
          <input style={{ ...S.thresholdInput, background:T.input, border:`1px solid ${T.inputBorder}`, color:T.text }}
            type="number" min="1" max="30"
            value={(settings.payments || {}).linkExpiryDays ?? 3}
            onChange={e => setPayments({ linkExpiryDays: Math.min(30, Math.max(1, parseInt(e.target.value, 10) || 3)) })} />
          <span style={{ ...S.thresholdUnit, color:T.textSub }}>days</span>
        </div>
        <div style={{ fontSize:11, color:T.textSub, fontStyle:'italic', paddingLeft:2 }}>
          ⓘ Keeps a link tied to the moment it was sent — so a customer can't pay a stale amount days after parts were added, or pay twice after settling at the counter. Expired links show a "call or text the shop for a new link" message.
        </div>
      </div>
    </>
  );
}
